import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useApiPortalV1AssessmentsPlansInterventionsCreateMutation } from '@/store/generated/portal/assessments/plans-interventions';
import { useApiPortalV1AssessmentsPlansOrganizationCreateMutation } from '@/store/generated/portal/assessments/plans-organization';
import { ActionPlanApiTags } from "areas/recommendations/api/actionPlanApi";
import { emptySplitApi } from "@/store/emptyApi";
import { ActionPlan, Recommendation } from "../types";
import { ACTION_PLAN_TYPE_INTERVENTIONS, ACTION_PLAN_TYPE_TEAM_PROJECTS } from "../constants";
import {
  determineLeaderAndCategory as determineLeaderAndCategoryUtil,
  buildIndividualActionPlanPayloads,
} from "../utils/actionPlanSaver";

interface UseActionPlanOperationsParams {
  selectedAssessmentPeriodId: number | null;
  organizationId: number | null;
  currentUserId: number | null;
  currentUserDebriefSchedule: { id: number; name_uid?: string } | null;
  categoryDelegates: unknown[] | null;
  existingActionPlans: ActionPlan[] | undefined;
  actionPlanType?: number;
  teamId?: number;
}

/**
 * Minimal hook that wraps the RTK mutation and utility functions for saving action plans.
 * All business logic has been moved to utils/actionPlanSaver.ts
 */
export const useActionPlanOperations = ({
  selectedAssessmentPeriodId,
  organizationId,
  currentUserId,
  currentUserDebriefSchedule,
  categoryDelegates,
  existingActionPlans,
  actionPlanType = ACTION_PLAN_TYPE_TEAM_PROJECTS,
  teamId,
}: UseActionPlanOperationsParams) => {
  const dispatch = useDispatch();
  const [organizationCreatePlan, { isLoading: isSavingOrganization }] = useApiPortalV1AssessmentsPlansOrganizationCreateMutation();
  const [interventionsCreatePlan, { isLoading: isSavingInterventions }] = useApiPortalV1AssessmentsPlansInterventionsCreateMutation();

  const isSaving = actionPlanType === ACTION_PLAN_TYPE_INTERVENTIONS ? isSavingInterventions : isSavingOrganization;

  const determineLeaderAndCategory = useCallback(
    () => determineLeaderAndCategoryUtil({
      currentUserDebriefSchedule,
      categoryDelegates,
      organizationId,
      currentUserId,
      selectedAssessmentPeriodId,
      existingActionPlans,
      actionPlanType,
      teamId,
    }),
    [currentUserDebriefSchedule, categoryDelegates, organizationId, currentUserId, selectedAssessmentPeriodId, existingActionPlans, actionPlanType, teamId]
  );

  const saveAllTasks = useCallback(
    async (
      tasks: Recommendation[],
      leaderId: number | null,
      categoryUid: string | undefined
    ): Promise<{ success: boolean; error?: string }> => {
      if (!selectedAssessmentPeriodId || !organizationId) {
        return { success: false, error: 'Missing required data' };
      }

      // Only create records for tasks that do not have a server-side action_plan_id yet
      const unsavedTasks = tasks.filter((t) => !(t as any).action_plan_id && !(t as any).id);

      // Deduplicate by recommendation_template_id + task_index within this save
      const uniqueByKey = new Map<string, Recommendation>();
      unsavedTasks.forEach((t) => {
        const key = `${t.recommendation_template_id ?? 'unknown'}_${t.task_index ?? 0}`;
        if (!uniqueByKey.has(key)) uniqueByKey.set(key, t);
      });

      const tasksToCreate = Array.from(uniqueByKey.values());

      const payloads = buildIndividualActionPlanPayloads(
        tasksToCreate,
        {
          selectedAssessmentPeriodId,
          organizationId,
          currentUserId,
          currentUserDebriefSchedule,
          categoryDelegates,
          existingActionPlans,
          actionPlanType,
          teamId,
        },
        leaderId,
        categoryUid
      );

      const toCreate = payloads;

      try {
        if (toCreate.length > 0) {
          // Fire individual creates in parallel for items not already present
          await Promise.all(
            toCreate.map((p) => {
              if (actionPlanType === ACTION_PLAN_TYPE_INTERVENTIONS) {
                return interventionsCreatePlan({ interventionActionPlan: p }).unwrap();
              }
              return organizationCreatePlan({ organizationAssessmentPeriodActionPlan: p }).unwrap();
            })
          );
        }
        // Invalidate ActionPlan tag so the list refreshes
        dispatch(emptySplitApi.util.invalidateTags([ActionPlanApiTags.ActionPlan]));
        return { success: true };
      } catch (error: any) {
        console.error('Failed to save action plan tasks (individual creation):', error);
        const origStatus = error?.status;
        const origDetail = error?.data?.detail || error?.message || String(error);
        return { success: false, error: `Failed to save individual action plans: ${origStatus || ''} ${origDetail}` };
      }
    },
    [selectedAssessmentPeriodId, organizationId, actionPlanType, teamId, dispatch, currentUserDebriefSchedule, categoryDelegates, currentUserId, existingActionPlans]
  );

  return {
    saveAllTasks,
    determineLeaderAndCategory,
    isSaving,
  };
};



